import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/nextjs';

/**
 * @returns {boolean}
 */
const isValidEnv = (): boolean => {
    const isWindow: boolean = typeof window !== 'undefined';
    return isWindow;
};

/**
 * @param {string} stringValue
 * @returns {null | boolean | number | Record<string, unknown> | []}
 */
const getParsedJSON = (
    stringValue: string,
): null | boolean | number | Record<string, unknown> | [] => {
    if (typeof stringValue !== 'string') return null;

    try {
        const result = JSON.parse(stringValue);

        return result;
    } catch (err) {
        return null;
    }
};

/**
 * @param {string} key
 * @param {string} initialValue
 */
const useStorage = <T>(
    key: string,
    initialValue: T,
): readonly [T | null, (newValue: T) => void] => {
    const [value, setValue] = useState<T>(initialValue);

    useEffect(() => {
        if (!isValidEnv()) return;

        const result: string | null = window.localStorage.getItem(key);

        if (result === null) {
            setValue(initialValue);
            return;
        }

        try {
            const parsedResult: T = JSON.parse(result);
            setValue(parsedResult);
        } catch (err) {
            Sentry.captureException(err);
            console.error(err);
            setValue(null);
        }
    }, [key, initialValue]);

    /**
     * @param {T} newValue
     * @returns {void}
     */
    const setItem = (newValue: T): void => {
        setValue(newValue);

        if (!isValidEnv()) return;
        window.localStorage.setItem(key, JSON.stringify(newValue));
    };

    /**
     * @param {StorageEvent} event
     * @returns {void}
     */
    const handleStorage = useCallback(
        (event: StorageEvent) => {
            try {
                const parsedNewValue = getParsedJSON(event.newValue);
                if (parsedNewValue === null) return;

                const isEqualKey: boolean = event.key === key;
                const isEqualValue: boolean = parsedNewValue === value;

                if (isEqualKey && !isEqualValue) {
                    setValue(parsedNewValue as T);
                }
            } catch (err) {
                Sentry.captureException(err, {
                    extra: {
                        newValue: JSON.stringify(event?.newValue),
                        value: JSON.stringify(value),
                    },
                });
                setValue(null);
            }
        },
        [key, value],
    );

    useEffect(() => {
        window.addEventListener('storage', handleStorage);
        return () => window.removeEventListener('storage', handleStorage);
    }, [handleStorage]);

    return [value, setItem] as const;
};

export default useStorage;
